import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../../services/api';

const POLL_INTERVAL = 3000;
const API_BASE = '/api/exports/jobs/';

const useExtractionExport = () => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const startExport = useCallback(
    async (mergedFilters, searchData) => {
      setIsExporting(true);
      setProgress(0);
      setError(null);
      stopPolling();

      try {
        const treatmentTypeLabel =
          searchData?.selectionLabels?.treatment_type?.[0]?.label?.toLowerCase() || '';

        const cultureLabels =
          searchData?.selectionLabels?.['treatment_cultures.culture'] ||
          searchData?.selectionLabels?.cultures ||
          [];
        const enemyLabels =
          searchData?.selectionLabels?.['treatment_enemies.enemy'] || [];

        const payload = {
          entity_type: 'treatment_extraction',
          selected_fields: {},
          filters: mergedFilters || {},
          extraction_meta: {
            treatment_type_label: treatmentTypeLabel,
            search_selections: {
              cultures: cultureLabels,
              enemies: enemyLabels,
            },
          },
        };

        const { data: jobData } = await axiosInstance.post(API_BASE, payload);
        const jobId = jobData.id;

        pollingRef.current = setInterval(async () => {
          try {
            const { data: statusData } = await axiosInstance.get(`${API_BASE}${jobId}/`);
            setProgress(statusData.progress || 0);

            if (statusData.status === 'completed') {
              stopPolling();
              setIsExporting(false);
              setProgress(100);

              if (statusData.download_url) {
                window.open(statusData.download_url, '_blank');
              }
              message.success(t('treatment-search|extraction.export_success'));
            } else if (statusData.status === 'failed') {
              stopPolling();
              setIsExporting(false);
              const errMsg = statusData.error_message || t('treatment-search|extraction.export_error');
              setError(errMsg);
              message.error(errMsg);
            }
          } catch (pollErr) {
            stopPolling();
            setIsExporting(false);
            const errMsg = pollErr?.response?.data?.error || pollErr?.response?.data?.message || pollErr.message;
            setError(errMsg);
            message.error(errMsg);
          }
        }, POLL_INTERVAL);
      } catch (err) {
        setIsExporting(false);
        const errMsg = err?.response?.data?.error || err?.response?.data?.message || err.message;
        setError(errMsg);
        message.error(errMsg);
      }
    },
    [stopPolling, t]
  );

  return { isExporting, progress, error, startExport };
};

export default useExtractionExport;
